import { useMemo } from "react";
import { Server, Layers } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { SectionCard, Row, InfoNote } from "@/components/common/Cards";
import { Sel } from "@/components/common/Sel";
import { fitCount } from "@/lib/engines/hosting";
import { fmtNum, fmtRp } from "@/lib/format";

export default function CapacityPanel({ fleet, packages, capacityPackageId, setCapacityPackageId }) {
  const options = [
    { value: "", label: "— pilih paket untuk simulasi kapasitas —" },
    ...packages.map((p) => ({ value: p.id, label: p.name })),
  ];
  const pkg = packages.find((p) => p.id === capacityPackageId);

  const rows = useMemo(() => {
    return (fleet.compute || []).map((r) => {
      const cap = r.cap;
      const alloc = r.alloc;
      const rem = r.remaining;
      const usagePct = {
        vcpu: cap.vcpu ? (alloc.vcpu / cap.vcpu) * 100 : 0,
        ram: cap.ram ? (alloc.ram_gb / cap.ram) * 100 : 0,
        disk: cap.disk ? (alloc.disk_gb / cap.disk) * 100 : 0,
      };
      const fit = pkg ? fitCount(rem, pkg) : null;
      return { r, cap, alloc, rem, usagePct, fit };
    });
  }, [fleet, pkg]);

  const totalFit = pkg ? rows.reduce((a, x) => a + (x.fit || 0), 0) : 0;
  const totalFitFull = pkg ? fleet.compute.reduce((a, x) => a + (x.cap ? Math.floor(Math.min(...[pkg.type !== "shared" ? x.cap.vcpu / Math.max(1, pkg.vcpu || 1) : Infinity, pkg.type !== "shared" ? x.cap.ram / Math.max(1, pkg.ramGb || 1) : Infinity, x.cap.disk / Math.max(1, pkg.diskGb || 1)].filter(Number.isFinite))) : 0), 0) : 0;

  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-5">
        <SectionCard title="Pool rata-rata" eyebrow="Kapasitas gabungan" testid="hosting-pool-card">
          <Row label="vCPU usable (overcommit + reserve)" value={`${fmtNum(Math.round(fleet.pool.cap.vcpu))}`} />
          <Row label="RAM usable" value={`${fmtNum(Math.round(fleet.pool.cap.ram))} GB`} />
          <Row label="Disk usable" value={`${fmtNum(Math.round(fleet.pool.cap.disk / 1000))} TB`} />
          <Row label="Biaya operasional" value={fmtRp(fleet.totalMonthly)} strong />
          <Row label="Biaya per vCPU / bulan" value={fmtRp(Math.round(fleet.pool.unit.cpu))} muted />
          <Row label="Biaya per GB RAM / bulan" value={fmtRp(Math.round(fleet.pool.unit.ram))} muted />
          <Row label="Biaya per GB Disk / bulan" value={fmtRp(Math.round(fleet.pool.unit.disk))} muted />
        </SectionCard>
        <SectionCard title="Simulasi paket vs kapasitas tersisa" eyebrow="Fit calculator" testid="hosting-fit-card">
          <Sel value={capacityPackageId || ""} onChange={(v) => setCapacityPackageId(v || null)} options={options} testid="hosting-fit-select" />
          {pkg ? (
            <div className="mt-4 space-y-2" data-testid="hosting-fit-result">
              <Row label="Total paket yang masih muat di fleet" value={fmtNum(totalFit)} strong />
              <Row label="Total kapasitas maksimum (tanpa alokasi existing)" value={fmtNum(totalFitFull)} muted />
              <InfoNote>
                Angka dihitung berdasarkan kapasitas setelah dikurangi VM yang sedang berjalan ({fleet.pool.alloc.count} VM).
              </InfoNote>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-3">Pilih salah satu paket untuk melihat berapa banyak lagi yang masih muat di fleet.</p>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Kapasitas per server" bodyClassName="p-0" testid="hosting-capacity-card">
        <div className="overflow-x-auto scroll-gold">
          <Table className="dense-table">
            <TableHeader>
              <TableRow className="bg-secondary hover:bg-secondary">
                <TableHead>Hostname</TableHead>
                <TableHead>Lokasi</TableHead>
                <TableHead className="text-right">vCPU</TableHead>
                <TableHead className="text-right">RAM</TableHead>
                <TableHead className="text-right">Disk</TableHead>
                <TableHead className="text-right">Biaya/bln</TableHead>
                {pkg && <TableHead className="text-right">Sisa muat</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ r, cap, alloc, rem, usagePct, fit }) => (
                <TableRow key={r.server.id} data-testid={`hosting-cap-${r.server.hostname}`}>
                  <TableCell className="font-medium">{r.server.hostname}</TableCell>
                  <TableCell className="text-muted-foreground">{r.server.location}</TableCell>
                  <TableCell className="text-right">
                    <div className="num">{fmtNum(alloc.vcpu)} / {fmtNum(Math.round(cap.vcpu))}</div>
                    <Progress value={Math.min(100, usagePct.vcpu)} className="h-1 mt-1 bg-[hsl(220_10%_14%)]" />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="num">{fmtNum(alloc.ram_gb)} / {fmtNum(Math.round(cap.ram))} GB</div>
                    <Progress value={Math.min(100, usagePct.ram)} className="h-1 mt-1 bg-[hsl(220_10%_14%)]" />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="num">{fmtNum(Math.round(alloc.disk_gb))} / {fmtNum(Math.round(cap.disk))} GB</div>
                    <Progress value={Math.min(100, usagePct.disk)} className="h-1 mt-1 bg-[hsl(220_10%_14%)]" />
                  </TableCell>
                  <TableCell className="text-right num">{fmtRp(r.totalCost)}</TableCell>
                  {pkg && (
                    <TableCell className="text-right">
                      <Badge variant="outline" className="border-[color:var(--hairline)] text-[hsl(43_60%_66%)] num">
                        {fmtNum(fit || 0)}
                      </Badge>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {fleet.compute.length === 0 && (
                <TableRow>
                  <TableCell colSpan={pkg ? 7 : 6} className="text-center p-8 text-muted-foreground">Belum ada server compute terpilih.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
    </div>
  );
}
